import React, { useEffect, useRef, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { styled } from "@mui/material";
import Box from "@mui/material/Box";
import Switch from "@mui/material/Switch"; 
import { setCesiumView } from 'redux/slices/mapbox/mapSlice';
import { selectFlightAction } from 'redux/slices/flight/selected';
import Loader from "components/Common/Loader";

const ToolbarStyled = styled(Box)(({ theme }) => ({
  position: "absolute",
  top: "4px", 
  right: "10px", 
  zIndex: 1000, 
  backgroundColor: theme.palette.primary.dark, 
  padding: theme.spacing(0.5), 
  borderRadius: "8px", 
}));

const LoadingOverlay = styled(Box)({
  position: 'absolute',
  top: 0,
  left: 0,
  width: '100%',
  height: '100%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  backgroundColor: 'rgba(0, 0, 0, 0.5)',
  zIndex: 999
});

const ModeLabel = styled('span')(({ theme, isChecked }) => ({
  fontWeight: 'bold',
  color: theme.palette.secondary, 
  marginRight: isChecked ? '0' : '8px', 
  marginLeft: isChecked ? '8px' : '0',
}));

const CesiumMap = () => {
  const iframeRef = useRef(null);
  const dispatch = useDispatch();
  const flights = useSelector((state) => state.flightList.data);
  const selectedFlight = useSelector((state) => state.flightSelected);
  const flightUuId = selectedFlight?.uuid ?? flights?.find((fid) => fid.id === selectedFlight?.id)?.uuid;
  const tilesApiUrl = flightUuId
    ? `${process.env.REACT_APP_BACKEND_URL}/api/3d_tileset_file/${flightUuId}/tileset.json`
    : null;
  const is3D = useSelector((state) => state.mapState.is3D);
  const [cesium, setCesium] = useState(is3D);
  const [loading, setLoading] = useState(true); 

  const switchtoCesium = () => {
    setCesium((prevCesium) => {
      const updatedCesium = !prevCesium;
      dispatch(setCesiumView(updatedCesium));
      if(!updatedCesium) {
        dispatch(selectFlightAction({ flight : selectedFlight }));
      }
      return updatedCesium;
    });
  };

  useEffect(() => {
    if (!flightUuId || !tilesApiUrl) {
      setLoading(false);
      return;
    }

    // Hide loading after a short delay to let iframe start loading
    const timer = setTimeout(() => {
      setLoading(false);
    }, 2000);

    return () => {
      clearTimeout(timer);
    };
  }, [flightUuId, tilesApiUrl]);

  // Build iframe URL with query parameters
  const getIframeUrl = () => {
    if (!tilesApiUrl) return null;
    
    const authToken = localStorage.getItem('token');
    const params = new URLSearchParams({
      tilesUrl: tilesApiUrl
    });
    
    if (authToken) {
      params.append('token', authToken);
    }
    
    if (process.env.REACT_APP_CESIUM_ION_TOKEN) {
      params.append('ionToken', process.env.REACT_APP_CESIUM_ION_TOKEN);
    }
    
    return `/cesium-viewer.html?${params.toString()}`;
  };

  return (
    <>
      {tilesApiUrl && (
        <iframe
          ref={iframeRef}
          src={getIframeUrl()}
          style={{
            width: '100%',
            height: '100vh',
            border: 'none',
            display: 'block'
          }}
          title="Cesium 3D Tiles Viewer"
          onLoad={() => {
            setLoading(false);
          }}
        />
      )}
      
      {loading && (
        <Loader />
      )}

      <ToolbarStyled>
        <ModeLabel isChecked={false}>2D</ModeLabel>
          <Switch
            checked={is3D}
            onChange={switchtoCesium}
            color="primary"
            disabled={!is3D}
          />
        <ModeLabel isChecked={false}>3D</ModeLabel>
      </ToolbarStyled>
    </>
  );
};

export default CesiumMap;
